import { CARD_TYPES, GRID_SIZE } from "../constants/editor";
import { createDefaultCardDraft, createEmptyGrid, type CardDraft, type CardType, type SceneLayerSlot } from "../shared/game";

type MutableGrid = ReturnType<typeof createEmptyGrid>;

export type SpritePreset = {
  id: string;
  name: string;
  kind: SceneLayerSlot;
  typeLabel: CardType["label"];
  typeKey: string;
  typeColor: string;
  desc: string;
  grid: MutableGrid;
};

function px(grid: MutableGrid, row: number, col: number, color: string): void {
  if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
    grid[row][col] = color;
  }
}

function rect(
  grid: MutableGrid,
  rowStart: number,
  colStart: number,
  rowEnd: number,
  colEnd: number,
  color: string,
): void {
  for (let row = Math.max(0, rowStart); row <= Math.min(GRID_SIZE - 1, rowEnd); row += 1) {
    for (let col = Math.max(0, colStart); col <= Math.min(GRID_SIZE - 1, colEnd); col += 1) {
      grid[row][col] = color;
    }
  }
}

function createSceneLayerForSprite(slot: SceneLayerSlot, spriteId: string | null, grid?: MutableGrid) {
  if (!spriteId || !grid) {
    return { slot, spriteId: null, offsetX: 0, offsetY: 0, scale: 1, visible: true } as const;
  }

  let minRow = GRID_SIZE;
  let maxRow = -1;
  let minCol = GRID_SIZE;
  let maxCol = -1;

  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      if (!grid[row][col]) {
        continue;
      }
      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);
      minCol = Math.min(minCol, col);
      maxCol = Math.max(maxCol, col);
    }
  }

  const width = maxCol - minCol + 1;
  const height = maxRow - minRow + 1;
  const offsetX = Math.round((GRID_SIZE - width) / 2);
  const offsetY = slot === "character"
    ? Math.round(GRID_SIZE - height - 1)
    : Math.round((GRID_SIZE - height) / 2);

  return { slot, spriteId, offsetX, offsetY, scale: 1, visible: true } as const;
}

function buildEmbyr32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const R = "#e94560";
  const O = "#f5a623";
  const Y = "#f8e71c";
  const DR = "#c0283a";
  rect(grid, 0, 11, 3, 12, R);
  rect(grid, 0, 19, 3, 20, R);
  px(grid, 0, 12, Y);
  px(grid, 0, 19, Y);
  rect(grid, 3, 9, 10, 22, K);
  rect(grid, 4, 10, 9, 21, R);
  rect(grid, 5, 11, 8, 20, DR);
  rect(grid, 5, 11, 7, 13, K);
  px(grid, 6, 12, W);
  px(grid, 6, 13, W);
  rect(grid, 5, 18, 7, 20, K);
  px(grid, 6, 18, W);
  px(grid, 6, 19, W);
  rect(grid, 8, 13, 9, 18, K);
  px(grid, 8, 14, O);
  px(grid, 8, 15, K);
  px(grid, 8, 16, K);
  px(grid, 8, 17, O);
  px(grid, 9, 14, R);
  px(grid, 9, 17, R);
  rect(grid, 10, 13, 12, 18, K);
  rect(grid, 11, 14, 11, 17, R);
  rect(grid, 12, 8, 24, 24, K);
  rect(grid, 13, 9, 23, 23, R);
  rect(grid, 14, 11, 22, 21, DR);
  rect(grid, 15, 12, 22, 20, O);
  rect(grid, 16, 13, 21, 19, Y);
  rect(grid, 13, 1, 19, 8, K);
  rect(grid, 14, 2, 18, 7, R);
  rect(grid, 15, 3, 17, 6, O);
  rect(grid, 19, 0, 22, 5, K);
  rect(grid, 20, 1, 21, 4, R);
  rect(grid, 13, 24, 19, 30, K);
  rect(grid, 14, 25, 18, 29, R);
  rect(grid, 15, 26, 17, 28, O);
  rect(grid, 19, 27, 22, 31, K);
  rect(grid, 20, 28, 21, 31, R);
  rect(grid, 23, 10, 28, 14, K);
  rect(grid, 24, 11, 27, 13, R);
  rect(grid, 23, 18, 28, 22, K);
  rect(grid, 24, 19, 27, 21, R);
  rect(grid, 27, 9, 28, 15, K);
  rect(grid, 27, 17, 28, 23, K);
  for (const col of [9, 11, 13]) px(grid, 28, col, O);
  for (const col of [17, 19, 21]) px(grid, 28, col, O);
  rect(grid, 22, 23, 25, 26, K);
  px(grid, 23, 24, R);
  px(grid, 24, 25, R);
  px(grid, 25, 26, O);
  px(grid, 26, 27, O);
  px(grid, 27, 28, Y);
  px(grid, 28, 29, Y);
  px(grid, 29, 30, Y);
  px(grid, 30, 31, O);
  px(grid, 31, 31, Y);
  return grid;
}

function buildAquble32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const B = "#4d96ff";
  const L = "#87ceeb";
  const C = "#50e3c2";
  const CB = "#0f3460";
  rect(grid, 1, 6, 14, 25, K);
  rect(grid, 2, 7, 13, 24, B);
  rect(grid, 3, 8, 12, 23, L);
  rect(grid, 4, 10, 10, 21, W);
  px(grid, 2, 7, K);
  px(grid, 2, 24, K);
  rect(grid, 5, 11, 9, 16, W);
  rect(grid, 5, 12, 8, 15, L);
  px(grid, 3, 9, W);
  px(grid, 3, 10, W);
  px(grid, 4, 9, W);
  px(grid, 3, 11, W);
  rect(grid, 7, 9, 10, 12, K);
  px(grid, 8, 10, CB);
  px(grid, 8, 11, CB);
  px(grid, 9, 12, W);
  rect(grid, 7, 19, 10, 22, K);
  px(grid, 8, 20, CB);
  px(grid, 8, 21, CB);
  px(grid, 9, 22, W);
  for (const col of [12, 13, 14, 15, 16, 17, 18, 19]) px(grid, 11, col, K);
  px(grid, 12, 11, K);
  px(grid, 12, 20, K);
  rect(grid, 13, 7, 16, 24, C);
  for (let col = 8; col <= 23; col += 2) px(grid, 16, col, L);
  for (const baseCol of [8, 11, 14, 17, 20, 23]) {
    for (let row = 17; row <= 31; row += 1) {
      const wave = Math.floor((row - 17) / 2) % 2 === 0 ? 0 : 1;
      px(grid, row, baseCol + wave, row % 4 < 2 ? B : C);
    }
  }
  return grid;
}

function buildSproat32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const G = "#6bcb77";
  const DG = "#3a7a45";
  const LG = "#98fb98";
  const P = "#ff69b4";
  const Y = "#f8e71c";
  const Br = "#8b4513";
  const DBr = "#5c2d0a";
  for (const [dr, dc] of [[-3, 0], [-2, -2], [-2, 2], [-1, -3], [-1, 3]]) {
    rect(grid, 4 + dr, 14 + dc, 5 + dr, 15 + dc, P);
  }
  rect(grid, 2, 14, 5, 17, Y);
  px(grid, 3, 15, K);
  px(grid, 3, 16, K);
  px(grid, 4, 15, K);
  rect(grid, 5, 15, 9, 16, DG);
  rect(grid, 7, 7, 11, 14, K);
  rect(grid, 8, 8, 10, 13, G);
  rect(grid, 9, 9, 9, 12, LG);
  rect(grid, 7, 17, 11, 24, K);
  rect(grid, 8, 18, 10, 23, G);
  rect(grid, 9, 18, 9, 21, LG);
  rect(grid, 9, 11, 25, 20, K);
  rect(grid, 10, 12, 24, 19, G);
  rect(grid, 11, 13, 23, 18, DG);
  for (const [row, col] of [[13, 13], [13, 17], [16, 12], [16, 18], [19, 15], [21, 13], [21, 17]]) {
    px(grid, row, col, LG);
  }
  rect(grid, 12, 13, 14, 15, K);
  px(grid, 13, 14, W);
  px(grid, 12, 15, W);
  rect(grid, 12, 16, 14, 18, K);
  px(grid, 13, 17, W);
  px(grid, 12, 16, W);
  px(grid, 15, 13, K);
  px(grid, 15, 14, Y);
  px(grid, 15, 15, Y);
  px(grid, 15, 16, Y);
  px(grid, 15, 17, K);
  px(grid, 16, 14, K);
  px(grid, 16, 17, K);
  rect(grid, 24, 12, 29, 15, K);
  rect(grid, 25, 13, 28, 14, DG);
  rect(grid, 24, 17, 29, 20, K);
  rect(grid, 25, 18, 28, 19, DG);
  px(grid, 29, 11, Br);
  px(grid, 29, 13, Br);
  px(grid, 29, 15, Br);
  px(grid, 30, 10, DBr);
  px(grid, 30, 12, DBr);
  px(grid, 30, 14, DBr);
  px(grid, 30, 16, DBr);
  px(grid, 31, 9, DBr);
  px(grid, 31, 11, DBr);
  px(grid, 31, 15, DBr);
  px(grid, 31, 17, DBr);
  return grid;
}

function buildVolty32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const Y = "#f8e71c";
  const O = "#f5a623";
  const P = "#ff69b4";
  const DY = "#c4a800";
  rect(grid, 0, 5, 7, 11, K);
  rect(grid, 1, 6, 6, 10, Y);
  rect(grid, 2, 7, 4, 8, P);
  rect(grid, 0, 20, 7, 26, K);
  rect(grid, 1, 21, 6, 25, Y);
  rect(grid, 2, 22, 4, 24, P);
  rect(grid, 6, 4, 14, 27, K);
  rect(grid, 7, 5, 13, 26, Y);
  rect(grid, 8, 6, 12, 25, O);
  rect(grid, 9, 5, 12, 8, P);
  rect(grid, 9, 23, 12, 26, P);
  rect(grid, 8, 9, 11, 12, K);
  px(grid, 9, 10, W);
  px(grid, 9, 11, W);
  px(grid, 10, 9, DY);
  rect(grid, 8, 19, 11, 22, K);
  px(grid, 9, 19, W);
  px(grid, 9, 20, W);
  px(grid, 10, 22, DY);
  rect(grid, 11, 15, 13, 16, K);
  px(grid, 12, 15, P);
  px(grid, 12, 16, P);
  rect(grid, 13, 5, 25, 26, K);
  rect(grid, 14, 6, 24, 25, Y);
  rect(grid, 15, 7, 23, 24, O);
  rect(grid, 15, 10, 23, 21, Y);
  rect(grid, 16, 11, 22, 20, W);
  rect(grid, 24, 7, 29, 12, K);
  rect(grid, 25, 8, 28, 11, Y);
  rect(grid, 24, 19, 29, 24, K);
  rect(grid, 25, 20, 28, 23, Y);
  rect(grid, 28, 6, 30, 13, K);
  rect(grid, 29, 7, 29, 12, O);
  rect(grid, 28, 18, 30, 25, K);
  rect(grid, 29, 19, 29, 24, O);
  for (const [row, col] of [[13, 26], [14, 27], [15, 28], [16, 29], [17, 30], [18, 31], [19, 30], [20, 31], [21, 30], [22, 31], [23, 30], [24, 31]]) {
    px(grid, row, col, Y);
  }
  return grid;
}

function buildPhanty32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const PP = "#c77dff";
  const PL = "#dda0dd";
  const PB = "#4d96ff";
  const DP = "#7b2fbf";
  rect(grid, 1, 6, 20, 25, K);
  rect(grid, 2, 7, 19, 24, PP);
  rect(grid, 3, 8, 18, 23, PL);
  rect(grid, 5, 9, 17, 22, PP);
  rect(grid, 7, 10, 16, 21, DP);
  px(grid, 2, 7, K);
  px(grid, 2, 24, K);
  px(grid, 3, 7, K);
  px(grid, 3, 24, K);
  rect(grid, 6, 9, 10, 13, K);
  rect(grid, 7, 10, 9, 12, PB);
  px(grid, 8, 10, W);
  px(grid, 7, 12, W);
  rect(grid, 6, 18, 10, 22, K);
  rect(grid, 7, 19, 9, 21, PB);
  px(grid, 8, 21, W);
  px(grid, 7, 19, W);
  for (const col of [11, 13, 15, 17, 19, 20]) px(grid, 14, col, K);
  for (const col of [12, 14, 16, 18, 19]) px(grid, 15, col, K);
  px(grid, 16, 11, K);
  px(grid, 16, 20, K);
  rect(grid, 9, 2, 14, 6, K);
  rect(grid, 10, 3, 13, 5, PP);
  px(grid, 11, 3, PL);
  rect(grid, 9, 25, 14, 29, K);
  rect(grid, 10, 26, 13, 28, PP);
  px(grid, 11, 28, PL);
  const waveBaseRow = 20;
  for (let col = 7; col <= 24; col += 1) px(grid, waveBaseRow, col, K);
  for (let band = 0; band < 3; band += 1) {
    const rowStart = waveBaseRow + 1 + band * 4;
    for (let col = 7; col <= 24; col += 1) {
      const wave = Math.floor((col - 7) / 3) % 2 === 0 ? 0 : 1;
      px(grid, rowStart + wave, col, PP);
      px(grid, rowStart + wave + 1, col, band === 2 ? K : PL);
    }
  }
  return grid;
}

function buildUmbrix32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const UG = "#4a4a4a";
  const UP = "#533483";
  const LG = "#6e6e6e";
  rect(grid, 4, 19, 23, 31, UG);
  rect(grid, 5, 20, 22, 30, K);
  rect(grid, 6, 21, 21, 29, UG);
  rect(grid, 16, 22, 23, 31, W);
  rect(grid, 17, 23, 22, 30, LG);
  rect(grid, 10, 5, 21, 19, K);
  rect(grid, 11, 6, 20, 18, UG);
  rect(grid, 12, 7, 19, 17, LG);
  rect(grid, 2, 9, 11, 20, K);
  rect(grid, 3, 10, 10, 19, UG);
  rect(grid, 4, 11, 9, 18, LG);
  rect(grid, 0, 9, 4, 12, K);
  rect(grid, 1, 10, 3, 11, UG);
  rect(grid, 0, 18, 4, 21, K);
  rect(grid, 1, 19, 3, 20, UG);
  rect(grid, 5, 10, 8, 13, K);
  px(grid, 5, 10, UP);
  px(grid, 5, 11, UP);
  px(grid, 6, 12, W);
  px(grid, 7, 10, K);
  px(grid, 7, 11, K);
  rect(grid, 5, 17, 8, 20, K);
  px(grid, 5, 18, UP);
  px(grid, 5, 19, UP);
  px(grid, 6, 17, W);
  px(grid, 7, 18, K);
  px(grid, 7, 19, K);
  rect(grid, 8, 12, 11, 18, K);
  rect(grid, 9, 13, 10, 17, UG);
  px(grid, 8, 13, W);
  px(grid, 8, 14, W);
  px(grid, 8, 16, W);
  px(grid, 8, 17, W);
  px(grid, 10, 14, K);
  px(grid, 10, 15, K);
  px(grid, 10, 16, K);
  rect(grid, 20, 7, 27, 11, K);
  rect(grid, 21, 8, 26, 10, UG);
  rect(grid, 20, 13, 27, 17, K);
  rect(grid, 21, 14, 26, 16, UG);
  rect(grid, 26, 6, 28, 12, K);
  rect(grid, 27, 7, 27, 11, LG);
  rect(grid, 26, 12, 28, 18, K);
  rect(grid, 27, 13, 27, 17, LG);
  return grid;
}

function buildStonk32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const Gr = "#9b9b9b";
  const Dk = "#4a4a4a";
  const Y = "#f8e71c";
  const LG = "#c8c8c8";
  rect(grid, 7, 5, 26, 26, K);
  rect(grid, 8, 6, 25, 25, Gr);
  rect(grid, 9, 7, 24, 24, LG);
  rect(grid, 9, 7, 12, 11, Dk);
  rect(grid, 13, 15, 17, 20, Dk);
  rect(grid, 19, 7, 23, 12, Dk);
  rect(grid, 11, 18, 15, 22, Dk);
  rect(grid, 20, 18, 24, 23, Dk);
  for (const [row, col] of [[10, 13], [12, 16], [15, 9], [17, 22], [22, 14]]) px(grid, row, col, Dk);
  rect(grid, 1, 8, 7, 23, K);
  rect(grid, 2, 9, 6, 22, Gr);
  rect(grid, 3, 10, 5, 21, LG);
  rect(grid, 2, 9, 4, 12, Dk);
  rect(grid, 2, 19, 4, 22, Dk);
  rect(grid, 3, 10, 6, 13, K);
  px(grid, 3, 11, Y);
  px(grid, 3, 12, Y);
  px(grid, 4, 11, Y);
  px(grid, 5, 12, W);
  rect(grid, 3, 19, 6, 22, K);
  px(grid, 3, 19, Y);
  px(grid, 3, 20, Y);
  px(grid, 4, 20, Y);
  px(grid, 5, 19, W);
  rect(grid, 6, 12, 8, 19, K);
  for (const col of [13, 14, 15, 16, 17, 18]) px(grid, 7, col, Dk);
  rect(grid, 9, 0, 17, 5, K);
  rect(grid, 10, 1, 16, 4, Gr);
  rect(grid, 11, 2, 15, 3, LG);
  rect(grid, 11, 1, 13, 2, Dk);
  rect(grid, 9, 26, 17, 31, K);
  rect(grid, 10, 27, 16, 30, Gr);
  rect(grid, 11, 28, 15, 30, LG);
  rect(grid, 11, 29, 13, 30, Dk);
  rect(grid, 25, 7, 30, 13, K);
  rect(grid, 26, 8, 29, 12, Gr);
  rect(grid, 25, 18, 30, 24, K);
  rect(grid, 26, 19, 29, 23, Gr);
  rect(grid, 29, 6, 31, 14, K);
  rect(grid, 30, 7, 30, 13, LG);
  rect(grid, 29, 17, 31, 25, K);
  rect(grid, 30, 18, 30, 24, LG);
  return grid;
}

function buildZephyr32() {
  const grid = createEmptyGrid();
  const K = "#1a1a2e";
  const W = "#ffffff";
  const T = "#50e3c2";
  const L = "#87ceeb";
  const O = "#f5a623";
  const Y = "#f8e71c";
  const DT = "#1a8a7a";
  rect(grid, 22, 9, 31, 22, K);
  rect(grid, 23, 10, 30, 21, T);
  rect(grid, 24, 11, 29, 20, L);
  rect(grid, 25, 12, 28, 19, W);
  for (let col = 10; col <= 21; col += 2) {
    px(grid, 30, col, DT);
    px(grid, 31, col, T);
  }
  rect(grid, 6, 0, 18, 9, K);
  rect(grid, 7, 1, 17, 8, T);
  rect(grid, 8, 2, 16, 7, L);
  rect(grid, 9, 3, 15, 6, W);
  for (let row = 8; row <= 17; row += 2) {
    px(grid, row, 0, DT);
    px(grid, row + 1, 1, T);
  }
  rect(grid, 6, 22, 18, 31, K);
  rect(grid, 7, 23, 17, 30, T);
  rect(grid, 8, 24, 16, 29, L);
  rect(grid, 9, 25, 15, 28, W);
  for (let row = 8; row <= 17; row += 2) {
    px(grid, row, 31, DT);
    px(grid, row + 1, 30, T);
  }
  rect(grid, 8, 10, 22, 21, K);
  rect(grid, 9, 11, 21, 20, W);
  rect(grid, 10, 12, 20, 19, L);
  rect(grid, 11, 13, 18, 17, T);
  rect(grid, 12, 14, 17, 16, W);
  rect(grid, 2, 10, 9, 21, K);
  rect(grid, 3, 11, 8, 20, W);
  rect(grid, 4, 12, 7, 19, L);
  px(grid, 1, 14, T);
  px(grid, 1, 15, T);
  px(grid, 1, 16, T);
  px(grid, 1, 17, T);
  px(grid, 0, 14, Y);
  px(grid, 0, 15, T);
  px(grid, 0, 16, DT);
  px(grid, 0, 17, Y);
  rect(grid, 5, 12, 7, 14, K);
  px(grid, 5, 12, L);
  px(grid, 6, 13, W);
  px(grid, 7, 12, K);
  rect(grid, 5, 17, 7, 19, K);
  px(grid, 5, 18, L);
  px(grid, 6, 17, W);
  px(grid, 7, 18, K);
  rect(grid, 7, 15, 10, 18, K);
  rect(grid, 8, 16, 9, 17, O);
  px(grid, 10, 16, Y);
  px(grid, 10, 17, Y);
  rect(grid, 13, 12, 16, 19, T);
  rect(grid, 14, 13, 15, 18, DT);
  return grid;
}

function buildLavaField32() {
  const grid = createEmptyGrid();
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      if (row < 18) {
        grid[row][col] = row % 2 === 0 ? "#1b1330" : "#221841";
      } else if (row < 24) {
        grid[row][col] = (row + col) % 3 === 0 ? "#ff7a45" : "#d9485f";
      } else {
        grid[row][col] = (row + col) % 2 === 0 ? "#f5a623" : "#ff6b35";
      }
    }
  }
  for (let col = 2; col < 30; col += 6) {
    rect(grid, 8, col, 13, col + 1, "#3e2d6b");
    px(grid, 7, col, "#7a5cff");
  }
  return grid;
}

function buildForestRuin32() {
  const grid = createEmptyGrid();
  rect(grid, 0, 0, 20, 31, "#122032");
  rect(grid, 21, 0, 31, 31, "#27462d");
  rect(grid, 0, 0, 5, 31, "#0b1425");
  rect(grid, 6, 2, 18, 7, "#355f39");
  rect(grid, 4, 24, 18, 29, "#355f39");
  rect(grid, 10, 12, 23, 19, "#7a6f58");
  rect(grid, 12, 13, 22, 18, "#a59678");
  rect(grid, 13, 14, 21, 17, "#d9cfb2");
  for (let col = 0; col < 32; col += 4) {
    px(grid, 22, col, "#6bcb77");
    px(grid, 24, col + 1, "#98fb98");
  }
  return grid;
}

function buildArcaneBurst32() {
  const grid = createEmptyGrid();
  const C = "#c77dff";
  const L = "#ffffff";
  for (let offset = 0; offset < 10; offset += 1) {
    px(grid, 16, 6 + offset, C);
    px(grid, 16, 25 - offset, C);
    px(grid, 6 + offset, 16, C);
    px(grid, 25 - offset, 16, C);
  }
  rect(grid, 13, 13, 18, 18, C);
  rect(grid, 14, 14, 17, 17, L);
  for (const [row, col] of [[9, 9], [9, 23], [23, 9], [23, 23], [11, 16], [21, 16], [16, 11], [16, 21]]) {
    px(grid, row, col, "#dda0dd");
  }
  return grid;
}

function buildStormFlash32() {
  const grid = createEmptyGrid();
  const B = "#4d96ff";
  const W = "#ffffff";
  for (let row = 0; row < 32; row += 4) {
    px(grid, row, 4 + (row % 8), B);
    px(grid, row + 1 < 32 ? row + 1 : row, 26 - (row % 6), "#87ceeb");
  }
  rect(grid, 4, 12, 8, 20, "#182f59");
  rect(grid, 5, 13, 9, 19, B);
  rect(grid, 6, 14, 8, 18, "#87ceeb");
  for (const [row, col] of [[9, 16], [12, 14], [14, 18], [17, 15], [20, 13], [23, 17], [26, 15]]) {
    px(grid, row, col, W);
    if (row + 1 < 32 && col - 1 >= 0) {
      px(grid, row + 1, col - 1, B);
    }
  }
  return grid;
}

function buildMoonTemple64() {
  const grid = createEmptyGrid();
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      if (row < 16) {
        grid[row][col] = row % 2 === 0 ? "#091325" : "#111a30";
      } else if (row < 42) {
        grid[row][col] = (row + col) % 3 === 0 ? "#17233a" : "#121c31";
      } else {
        grid[row][col] = (row + col) % 2 === 0 ? "#1f2632" : "#232b38";
      }
    }
  }
  rect(grid, 8, 44, 18, 54, "#f1f2ff");
  rect(grid, 9, 45, 17, 53, "#d7ddff");
  rect(grid, 10, 46, 16, 52, "#b6c1ff");
  rect(grid, 28, 20, 58, 43, "#6f6757");
  rect(grid, 30, 22, 57, 41, "#91866f");
  rect(grid, 32, 24, 56, 39, "#d3c6a6");
  for (let col = 22; col <= 40; col += 6) {
    rect(grid, 24, col, 57, col + 1, "#524a3f");
  }
  for (let col = 0; col < GRID_SIZE; col += 5) {
    px(grid, 46, col, "#8b7f6d");
    px(grid, 48, col + 2 < GRID_SIZE ? col + 2 : col, "#b4a48b");
  }
  return grid;
}

function buildSunsetDunes64() {
  const grid = createEmptyGrid();
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      if (row < 14) {
        grid[row][col] = row % 2 === 0 ? "#3e1d48" : "#54235c";
      } else if (row < 30) {
        grid[row][col] = (row + col) % 2 === 0 ? "#f07f6a" : "#d95d6c";
      } else {
        grid[row][col] = (row + col) % 3 === 0 ? "#e0ad56" : "#c88b3a";
      }
    }
  }
  rect(grid, 12, 8, 24, 20, "#ffcf6f");
  rect(grid, 13, 9, 23, 19, "#ffe3a6");
  for (let col = 0; col < GRID_SIZE; col += 1) {
    const duneA = 40 + Math.floor(Math.sin(col / 7) * 3);
    const duneB = 49 + Math.floor(Math.cos(col / 9) * 4);
    for (let row = duneA; row < GRID_SIZE; row += 1) {
      grid[row][col] = row < duneB ? "#e3b75f" : "#a66a2e";
    }
  }
  return grid;
}

function buildCrystalCavern64() {
  const grid = createEmptyGrid();
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      grid[row][col] = (row + col) % 2 === 0 ? "#0b1526" : "#111d30";
    }
  }
  rect(grid, 40, 0, 63, 63, "#1e2b4a");
  const clusters: Array<[number, number, string]> = [
    [18, 10, "#7cf1ff"],
    [24, 20, "#b38cff"],
    [12, 38, "#6bd1ff"],
    [30, 48, "#d69dff"],
    [42, 28, "#78ffd5"],
  ];
  for (const [row, col, color] of clusters) {
    rect(grid, row, col, row + 8, col + 2, color);
    rect(grid, row + 2, col - 2, row + 6, col + 4, color);
    px(grid, row - 1, col + 1, "#ffffff");
    px(grid, row + 9, col + 1, "#ffffff");
  }
  return grid;
}

function buildEmberRing64() {
  const grid = createEmptyGrid();
  const cx = 32;
  const cy = 34;
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      const dx = col - cx;
      const dy = row - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 18 && dist < 24) {
        grid[row][col] = dist > 21 ? "#ff6b35" : "#f8e71c";
      }
      if ((row + col) % 17 === 0 && dist > 15 && dist < 27) {
        grid[row][col] = "#ffffff";
      }
    }
  }
  return grid;
}

function buildTidalMist64() {
  const grid = createEmptyGrid();
  for (let col = 0; col < GRID_SIZE; col += 1) {
    const crest = 24 + Math.floor(Math.sin(col / 5) * 4);
    const mid = 34 + Math.floor(Math.cos(col / 8) * 5);
    for (let row = crest; row <= mid; row += 1) {
      grid[row][col] = row % 2 === 0 ? "#87ceeb" : "#50e3c2";
    }
    if (col % 6 === 0 && crest - 2 >= 0) {
      px(grid, crest - 2, col, "#ffffff");
      px(grid, crest - 1, col + 1 < GRID_SIZE ? col + 1 : col, "#dffaff");
    }
  }
  return grid;
}

function buildLeafGale64() {
  const grid = createEmptyGrid();
  const leaves = [
    [12, 10], [18, 22], [26, 16], [34, 30], [20, 42], [30, 50], [44, 40], [48, 20],
  ];
  for (const [row, col] of leaves) {
    rect(grid, row, col, row + 2, col + 4, "#6bcb77");
    px(grid, row + 1, col + 2, "#98fb98");
    px(grid, row + 3 < GRID_SIZE ? row + 3 : row, col + 1, "#3a7a45");
  }
  for (let i = 0; i < GRID_SIZE; i += 3) {
    const row = 10 + Math.floor(i / 2.5);
    const col = i;
    if (row < GRID_SIZE && col < GRID_SIZE) {
      px(grid, row, col, "#b6ffd2");
    }
  }
  return grid;
}

export const SPRITES: SpritePreset[] = [
  {
    id: "embyr",
    name: "Embyr",
    kind: "character",
    typeLabel: CARD_TYPES[0].label,
    typeKey: "Fire",
    typeColor: "#ff6b35",
    desc: "Born of lava flows. Embyr's wings ignite at dawn and never cool down.",
    grid: buildEmbyr32(),
  },
  {
    id: "aquble",
    name: "Aquble",
    kind: "character",
    typeLabel: CARD_TYPES[1].label,
    typeKey: "Water",
    typeColor: "#4d96ff",
    desc: "Drifts deep-ocean currents. Its tentacles glow to lure curious fish.",
    grid: buildAquble32(),
  },
  {
    id: "sproat",
    name: "Sproat",
    kind: "character",
    typeLabel: CARD_TYPES[2].label,
    typeKey: "Nature",
    typeColor: "#6bcb77",
    desc: "Carries a living flower on its head. Each petal blooms in a different season.",
    grid: buildSproat32(),
  },
  {
    id: "volty",
    name: "Volty",
    kind: "character",
    typeLabel: CARD_TYPES[3].label,
    typeKey: "Electric",
    typeColor: "#ffd93d",
    desc: "Stores lightning in its cheeks. A sneeze can power a city block.",
    grid: buildVolty32(),
  },
  {
    id: "phanty",
    name: "Phanty",
    kind: "character",
    typeLabel: CARD_TYPES[4].label,
    typeKey: "Mystic",
    typeColor: "#c77dff",
    desc: "Phases through walls at midnight and leaves behind lavender static.",
    grid: buildPhanty32(),
  },
  {
    id: "umbrix",
    name: "Umbrix",
    kind: "character",
    typeLabel: CARD_TYPES[5].label,
    typeKey: "Shadow",
    typeColor: "#9b9b9b",
    desc: "Hunts using its own shadow. Its tail tip glows white to confuse enemies.",
    grid: buildUmbrix32(),
  },
  {
    id: "stonk",
    name: "Stonk",
    kind: "character",
    typeLabel: CARD_TYPES[5].label,
    typeKey: "Shadow",
    typeColor: "#d2691e",
    desc: "Ancient and unmovable. Stonk has outlasted three mountain ranges.",
    grid: buildStonk32(),
  },
  {
    id: "zephyr",
    name: "Zephyr",
    kind: "character",
    typeLabel: CARD_TYPES[3].label,
    typeKey: "Electric",
    typeColor: "#50e3c2",
    desc: "Rides thermals above the clouds. Its feathers chime like crystal wind bells.",
    grid: buildZephyr32(),
  },
  {
    id: "lava-field",
    name: "Lava Field",
    kind: "background",
    typeLabel: CARD_TYPES[0].label,
    typeKey: "Fire",
    typeColor: "#ff6b35",
    desc: "A molten arena backdrop with rune pylons and burning seams.",
    grid: buildLavaField32(),
  },
  {
    id: "forest-ruin",
    name: "Forest Ruin",
    kind: "background",
    typeLabel: CARD_TYPES[2].label,
    typeKey: "Nature",
    typeColor: "#6bcb77",
    desc: "A mossy ruin clearing with a broken shrine at the center.",
    grid: buildForestRuin32(),
  },
  {
    id: "moon-temple",
    name: "Moon Temple",
    kind: "background",
    typeLabel: CARD_TYPES[4].label,
    typeKey: "Mystic",
    typeColor: "#c77dff",
    desc: "A silver moon above a ruined temple court.",
    grid: buildMoonTemple64(),
  },
  {
    id: "sunset-dunes",
    name: "Sunset Dunes",
    kind: "background",
    typeLabel: CARD_TYPES[0].label,
    typeKey: "Fire",
    typeColor: "#ffb347",
    desc: "Sweeping dunes under a heat-blasted sunset.",
    grid: buildSunsetDunes64(),
  },
  {
    id: "crystal-cavern",
    name: "Crystal Cavern",
    kind: "background",
    typeLabel: CARD_TYPES[1].label,
    typeKey: "Water",
    typeColor: "#87ceeb",
    desc: "A cavern lit by giant luminous crystal spires.",
    grid: buildCrystalCavern64(),
  },
  {
    id: "arcane-burst",
    name: "Arcane Burst",
    kind: "effect",
    typeLabel: CARD_TYPES[4].label,
    typeKey: "Mystic",
    typeColor: "#c77dff",
    desc: "A floating sigil ring that frames the creature with mystic energy.",
    grid: buildArcaneBurst32(),
  },
  {
    id: "storm-flash",
    name: "Storm Flash",
    kind: "effect",
    typeLabel: CARD_TYPES[3].label,
    typeKey: "Electric",
    typeColor: "#4d96ff",
    desc: "A crackling lightning veil for extra motion and impact.",
    grid: buildStormFlash32(),
  },
  {
    id: "ember-ring",
    name: "Ember Ring",
    kind: "effect",
    typeLabel: CARD_TYPES[0].label,
    typeKey: "Fire",
    typeColor: "#ff6b35",
    desc: "A hot ring of sparks and drifting embers.",
    grid: buildEmberRing64(),
  },
  {
    id: "tidal-mist",
    name: "Tidal Mist",
    kind: "effect",
    typeLabel: CARD_TYPES[1].label,
    typeKey: "Water",
    typeColor: "#50e3c2",
    desc: "A horizontal mist-wave for watery motion and depth.",
    grid: buildTidalMist64(),
  },
  {
    id: "leaf-gale",
    name: "Leaf Gale",
    kind: "effect",
    typeLabel: CARD_TYPES[2].label,
    typeKey: "Nature",
    typeColor: "#6bcb77",
    desc: "A storm of leaves and wind streaks around the fighter.",
    grid: buildLeafGale64(),
  },
];

export function spriteToDraft(spriteId: string): CardDraft {
  const sprite = SPRITES.find((entry) => entry.id === spriteId);
  if (!sprite) {
    return createDefaultCardDraft();
  }

  return {
    ...createDefaultCardDraft(),
    name: sprite.name,
    description: sprite.desc,
    typeLabel: sprite.typeLabel,
    sceneLayers: [
      createSceneLayerForSprite("background", null),
      createSceneLayerForSprite("character", sprite.id, sprite.grid),
      createSceneLayerForSprite("effect", null),
    ],
  };
}

export function getSpritePreset(spriteId: string | null | undefined): SpritePreset | null {
  if (!spriteId) {
    return null;
  }

  return SPRITES.find((entry) => entry.id === spriteId) ?? null;
}

export function getSpritesForSlot(slot: SceneLayerSlot): SpritePreset[] {
  return SPRITES.filter((sprite) => sprite.kind === slot);
}
